// Roulette — pure rules + spin physics. No React, no DOM, no money.
//
// Everything here is a pure function of its inputs so it can be unit-tested
// and, later, driven by a server-decided result without touching the visuals.
// Spec + research: docs/ROULETTE_PLAN.md
//
// EUROPEAN single-zero only. There is deliberately no 00 and no five-number
// bet — that combination exists only on the American wheel and carries a
// 7.89% edge (the worst bet on any roulette table).

/* ─── Wheel ──────────────────────────────────────────────────────── */

/** Clockwise pocket order of a real European wheel. NOT 1..36 — using
 *  sequential numbers around the rim is the most obvious "fake" tell. */
import { REST_FRAC } from './wheelGeometry';

export const WHEEL: readonly number[] = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10,
  5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
];

export const POCKETS = WHEEL.length;                 // 37
export const TAU = Math.PI * 2;
export const POCKET_ARC = TAU / POCKETS;             // 0.169813 rad ≈ 9.73°

const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

export type Colour = 'red' | 'black' | 'green';
export const colourOf = (n: number): Colour =>
  n === 0 ? 'green' : RED.has(n) ? 'red' : 'black';

export const mod = (a: number, m: number) => ((a % m) + m) % m;

/* ─── Bets ───────────────────────────────────────────────────────── */

export type BetKind =
  | 'straight' | 'split' | 'street' | 'corner' | 'sixline'
  | 'column' | 'dozen' | 'red' | 'black' | 'odd' | 'even' | 'low' | 'high';

/** Profit paid per 1 staked, i.e. "to 1". Total returned = stake × (payout+1).
 *  Every entry satisfies payout = 36/covered − 1, which is what makes the
 *  house edge a flat 2.70% (1/37) across the whole table. */
export const COVERED: Readonly<Record<BetKind, number>> = Object.freeze({
  straight: 1, split: 2, street: 3, corner: 4, sixline: 6,
  column: 12, dozen: 12,
  red: 18, black: 18, odd: 18, even: 18, low: 18, high: 18,
});

/** DERIVED, never hand-written: payout = 36/covered − 1. Deriving it is what
 *  guarantees the table can never drift out of step with the coverage counts —
 *  and a drift there is a silent change to the house edge. */
export const PAYOUT: Readonly<Record<BetKind, number>> = Object.freeze({
  ...Object.fromEntries(
    (Object.keys(COVERED) as BetKind[]).map((k) => [k, 36 / COVERED[k] - 1]),
  ),
  /* OWNER OVERRIDE (2026-09-01): the straight-up RETURN is 30x the stake.
     PAYOUT is profit-per-unit ("to 1"), so that is 29 here — every display
     surface shows RETURN_X = PAYOUT + 1, and the owner's "maximum a user
     should get is X30, thirty times" is then literally true. The rest keep
     the uniform 36/covered − 1 profit table (even money returns 2x, dozens
     3x — the same real payouts as before, only the LABELS moved to
     what-you-get). The server carries the identical override; the parity gate
     pins the tables together. */
  straight: 29,
} as Record<BetKind, number>);

/** What the player GETS BACK per unit staked — the number every surface shows.
 *  "X1" (profit-speak) told an even-money player they would merely get their
 *  own money back; the owner is right that no real table talks like that.
 *  X2 on red, X3 on a dozen, X30 on a number: the language of what lands in
 *  your wallet. Derived, so display can never drift from settlement. */
export const RETURN_X: Readonly<Record<BetKind, number>> = Object.freeze(
  Object.fromEntries(
    (Object.keys(PAYOUT) as BetKind[]).map((k) => [k, PAYOUT[k] + 1]),
  ) as Record<BetKind, number>,
);

/** Does this bet's shape match its kind? The engine must never pay a bet whose
 *  `numbers` disagree with its `kind` — {kind:'straight', numbers:[1..36]} would
 *  otherwise return 36x on a 36/37 chance. Today Board is the only constructor,
 *  but this is the exact seam a server will sit on, and `numbers` will arrive
 *  from the client. Cheap to check, catastrophic to skip. */
export function isValidBet(b: Bet): boolean {
  if (!Number.isFinite(b.amount) || b.amount <= 0) return false;
  const need = COVERED[b.kind];
  if (need === undefined || b.numbers.length !== need) return false;
  const seen = new Set<number>();
  for (const n of b.numbers) {
    if (!Number.isInteger(n) || n < 0 || n > 36 || seen.has(n)) return false;
    seen.add(n);
  }
  return true;
}

export type Bet = {
  id: string;          // stable key, e.g. "straight:17" or "split:1-2"
  kind: BetKind;
  numbers: number[];   // every number this bet covers
  amount: number;
};

/** Does `n` win this bet? Zero loses every outside bet — it is not red, black,
 *  odd, even, low or high, and belongs to no column or dozen. */
export function betWins(bet: Bet, n: number): boolean {
  return bet.numbers.includes(n);
}

/** Total returned to the player for one bet (stake + profit), 0 if it lost. */
export function betReturn(bet: Bet, n: number): number {
  if (!isValidBet(bet)) return 0;                       // shape guard, see isValidBet
  return betWins(bet, n) ? bet.amount * (PAYOUT[bet.kind] + 1) : 0;
}

export function settle(bets: Bet[], n: number) {
  let staked = 0, returned = 0;
  const winners: string[] = [];
  for (const b of bets) {
    staked += b.amount;
    const r = betReturn(b, n);
    if (r > 0) { returned += r; winners.push(b.id); }
  }
  return { staked, returned, net: returned - staked, winners };
}

/* ─── Table structure (derived, never read off the screen) ────────── */

export const columnNumbers = (c: 1 | 2 | 3) =>
  Array.from({ length: 12 }, (_, i) => i * 3 + c);              // c=1 → 1,4,7…
export const dozenNumbers = (d: 1 | 2 | 3) =>
  Array.from({ length: 12 }, (_, i) => (d - 1) * 12 + i + 1);
export const streetNumbers = (k: number) => {
  const r = Math.min(12, Math.max(1, Math.round(k)));    // k=0 gave [-2,-1,0] @ 11:1
  return [r * 3 - 2, r * 3 - 1, r * 3];
};

export const OUTSIDE: Record<'red'|'black'|'odd'|'even'|'low'|'high', number[]> = {
  red:   [...RED].sort((a, b) => a - b),
  black: Array.from({ length: 36 }, (_, i) => i + 1).filter((n) => !RED.has(n)),
  odd:   Array.from({ length: 36 }, (_, i) => i + 1).filter((n) => n % 2 === 1),
  even:  Array.from({ length: 36 }, (_, i) => i + 1).filter((n) => n % 2 === 0),
  low:   Array.from({ length: 18 }, (_, i) => i + 1),
  high:  Array.from({ length: 18 }, (_, i) => i + 19),
};

/* ─── Spin physics ───────────────────────────────────────────────── */
//
// The result is decided FIRST; the animation is then solved backwards so the
// ball arrives at that pocket. Exact for ANY easing curve, so the motion can
// be retuned freely without ever changing the outcome.
//
// CRITICAL: every angle is a pure function of elapsed time. Never accumulate
// `angle += speed` per frame — a 120Hz phone would run double speed and a
// backgrounded tab (rAF throttled to ~1Hz) would land the ball on the WRONG
// number. This app has already been bitten by that throttling behaviour.

export type Spin = {
  winner: number;
  T: number;          // seconds, launch → settle
  S: number;          // total ball sweep (radians, counter-clockwise)
  phiWin: number;     // winning pocket's angle in the rotor's frame
  eps: number;        // small resting offset so it never looks snapped
  ballA0: number;     // ball start angle
  rotorA0: number;    // rotor start angle
  wobbleSeed: number;
};

const ROTOR_W0 = 1.35;    // rad/s ≈ 13 rpm
const ROTOR_TAU = 240;    // s — decays over minutes, so it never visibly stops

/** IDLE rotor angle — the wheel between rounds.
 *
 *  A real wheel is nudged by the croupier long before it ever stops; through
 *  the peephole ours is on screen permanently, and a wheel that visibly winds
 *  down to a halt looks broken. So the idle speed BREATHES: it eases down,
 *  picks back up, eases down again, forever, and never reaches zero.
 *
 *      ω(t) = W0·(1 + A·sin(2πt/P))        with A < 1, so ω > 0 always
 *      θ(t) = ∫ω = W0·(t + (A·P/2π)·(1 − cos(2πt/P)))
 *
 *  Closed form and a pure function of elapsed time, like everything else here —
 *  never accumulated per frame, so a slow frame or a throttled tab cannot make
 *  it drift.
 */
const IDLE_W0 = 0.80;     // rad/s, the average
const IDLE_A = 0.62;      // how deep the breath is (0.38x .. 1.62x speed)
const IDLE_P = 13;        // seconds per breath — slow enough to feel natural
export const idleRotorAngle = (t: number) =>
  IDLE_W0 * (t + (IDLE_A * IDLE_P / TAU) * (1 - Math.cos((TAU * t) / IDLE_P)));

/** Rotor angle at time t. Closed form, so θ_rotor(T) is known before the
 *  spin starts — which is what makes the ball's target solvable. */
export const rotorAngle = (t: number, a0: number) =>
  a0 + ROTOR_W0 * ROTOR_TAU * (1 - Math.exp(-t / ROTOR_TAU));

// Two-phase ball easing. A single ease-out over the whole spin is what makes
// browser roulette "crawl" fakely at the end; a real ball dies suddenly, from
// collisions, at speed.
const XD = 0.78;    // fraction of the spin spent on the ball track
/** Fraction of the total sweep covered while still on the track — which fixes
 *  how far the ball travels RELATIVE TO THE ROTOR after first contact ("scatter",
 *  the thing that makes a real wheel unpredictable). At 0.955 this model gave
 *  ~24 pockets; a real wheel at our rotor speed (~13 rpm) gives 9-18, and 24 is
 *  what you would see off a much faster rotor. 0.978 lands it mid-range. */
const M = 0.978;
const RHO = 0.22;   // speed at drop ÷ speed at launch
/** Post-drop knee. SOLVED, not chosen: the hand-picked 1.95 made the ball
 *  speed up 18.8% at the exact moment it left the track — the opposite of what
 *  a real ball does. Velocity is continuous at XD iff
 *      C / (1 − e^−C) = [2·M·RHO / (XD·(1+RHO))] · (1 − XD) / (1 − M)
 *  Newton on that gives C ≈ 1.4763 for the constants above. Computing it here
 *  means retuning M/XD/RHO can never silently reintroduce the discontinuity. */
const C = (() => {
  const target = ((2 * M * RHO) / (XD * (1 + RHO))) * (1 - XD) / (1 - M);
  let c = 1.5;
  for (let i = 0; i < 60; i++) {
    const e = Math.exp(-c), d = 1 - e;
    const f = c / d - target;
    const df = (d - c * e) / (d * d);
    const step = f / df;
    c -= step;
    if (!Number.isFinite(c) || c <= 1e-6) { c = 1.4763; break; }
    if (Math.abs(step) < 1e-12) break;
  }
  return c;
})();

export function ease(x: number): number {
  if (x <= XD) {
    const u = x / XD;                                   // linear angular decel
    return (M * (u - (1 - RHO) * u * u / 2)) / (1 - (1 - RHO) / 2);
  }
  const w = (x - XD) / (1 - XD);                        // exponential decay
  return M + (1 - M) * (1 - Math.exp(-C * w)) / (1 - Math.exp(-C));
}

/* ─── THE SETTLE ──────────────────────────────────────────────────────────
 *
 * A real ball does not glide to a halt: it falls off the track, hits the rotor,
 * and BOUNCES — each bounce lower and quicker than the last, until it chatters
 * between the frets and drops in. That accelerating chatter is the sound and
 * the sight everyone recognises, and a smooth sinusoidal "hop" (what this used
 * to be) reads as fake precisely because its bounces were evenly spaced and
 * died smoothly.
 *
 * So: a genuine bouncing-ball model, in closed form.
 *
 * With coefficient of restitution E, a ball dropped from height H rebounds to
 * H·E², and each flight lasts E times the previous one. Writing the first fall
 * as τ and normalising the whole sequence into w ∈ [0,1]:
 *
 *      total = τ·(1 + 2E/(1−E)) = τ·(1+E)/(1−E) = 1   ⇒   τ = (1−E)/(1+E)
 *
 * Impact k lands at   t_k = τ·[1 + 2E·(1−E^(k−1))/(1−E)]
 * so the segment containing any w is found in O(1) with one logarithm — no loop
 * over frames, and still a pure function of elapsed time.
 *
 * Heights fall as E^(2k), durations as E^k: the bounces get SMALLER and CLOSER
 * TOGETHER, which is the whole feel. And because each segment is a parabola
 * pinned to zero at both ends, every impact is a true cusp — the vertical speed
 * flips from −v to +E·v — not a smooth turn.
 */
const E_BASE = 0.60;                       // restitution: ivorine ball on a hard rotor
const N_BOUNCE = 6;                        // beyond this the rebound is well under a pixel
/** 27 half-cycles across the track phase, so the rattle is exactly zero at XD. */
const TRACK_RATTLE_K = (27 * Math.PI) / XD;

/** Restitution for THIS spin. No two balls hit the same fret at the same angle,
 *  and a settle that plays out identically every round is the thing that makes
 *  a wheel look scripted. Varying E changes the number of bounces, their heights
 *  AND their timing together — one number, and no two settles look alike. */
function restitution(seed: number): number {
  const u = Math.abs((Math.sin(seed * 24.113) * 27183.845) % 1);
  return E_BASE + (u - 0.5) * 0.12;        // 0.54 .. 0.66
}

/** Which bounce segment w falls in, and how far through it we are.
 *  k = 0 is the initial fall; k ≥ 1 are the rebound arcs. */
function bouncePhase(w: number, E: number): { k: number; u: number } {
  const t0 = (1 - E) / (1 + E);            // the first fall, normalised
  if (w <= t0) return { k: 0, u: w / t0 };
  // invert t_k for k, then take the floor
  const r = 1 - ((w / t0 - 1) * (1 - E)) / (2 * E);
  if (!(r > 0)) return { k: N_BOUNCE, u: 1 };
  const k = Math.min(N_BOUNCE, Math.floor(Math.log(r) / Math.log(E)) + 1);
  const tk = t0 * (1 + (2 * E * (1 - Math.pow(E, k - 1))) / (1 - E));
  const dk = 2 * t0 * Math.pow(E, k);
  return { k, u: Math.min(1, Math.max(0, (w - tk) / dk)) };
}

/** Height above the pocket floor, as a fraction of the initial drop.
 *  h(0) = 1 (leaving the track), h(1) = 0 (at rest), cusp at every impact. */
export function bounceHeight(w: number, seed = 0): number {
  if (w <= 0) return 1;
  if (w >= 1) return 0;
  const E = restitution(seed);
  const { k, u } = bouncePhase(w, E);
  if (k === 0) return 1 - u * u;                       // the fall: h = 1 − u²
  if (k >= N_BOUNCE) return 0;
  return Math.pow(E, 2 * k) * 4 * u * (1 - u);         // each rebound arc
}

/** Angular scatter, synchronised to the bounces.
 *
 *  Every impact scuffs the ball sideways, so the jitter must happen ON the
 *  bounces rather than free-running past them — that synchronisation is what
 *  makes the motion read as one object hitting one surface. sin(2π·u) is zero
 *  at both ends of every segment, so the scatter vanishes exactly at each
 *  impact, and the (1−w) envelope forces it to exactly zero at w = 1.
 *
 *  Zero at w = 1 is not cosmetic: the landing angle is solved in advance, and
 *  any residue here would put the ball in the wrong pocket. */
export function wobble(x: number, seed = 0): number {
  if (x <= XD) return 0;
  const w = (x - XD) / (1 - XD);
  if (w >= 1) return 0;
  const E = restitution(seed);
  const { k, u } = bouncePhase(w, E);
  // The seed varies how hard this particular ball scuffs — never the phase,
  // which has to stay pinned to the impacts.
  //
  // NOTE the scuff alone is cut at N_BOUNCE, NOT the whole function. Returning
  // 0 for everything here dropped the capture rattle to zero in ONE FRAME while
  // it was still swinging — a measured 1.24px sideways teleport about 0.2s
  // before rest, right in the stretch the player is staring at.
  const u1 = Math.abs((Math.sin(seed * 12.9898) * 43758.5453) % 1);
  const A = 1.25 * POCKET_ARC * (0.65 + 0.7 * u1);
  const scuff = k >= N_BOUNCE ? 0 : A * Math.pow(E, 2 * k) * Math.sin(TAU * u);

  // CAPTURE RATTLE. By the last bounces the vertical hop is under a pixel, but
  // a real ball is not finished: it is trapped between frets, knocking back and
  // forth across a pocket or two, dropping in and hopping out again. That last
  // half second is where the player is actually watching, and without it the
  // ball just quietly arrives. Rides on top of the scuff, one to two pockets
  // wide, and the (1−w)² envelope takes it to exactly zero at rest — so the
  // landed number still cannot change.
  const RATTLE_FROM = 0.62;
  let rattle = 0;
  if (w > RATTLE_FROM) {
    const v = (w - RATTLE_FROM) / (1 - RATTLE_FROM);
    const u2 = Math.abs((Math.sin(seed * 51.771) * 19349.663) % 1);
    const AR = 0.95 * POCKET_ARC * (0.6 + 0.8 * u2);
    rattle = AR * Math.sin(TAU * (2.5 + u2) * v) * (1 - v) * (1 - v);
  }
  // The (1−v)² envelope already takes the rattle to exactly zero at w = 1, so
  // it must NOT also be multiplied by (1−w) — doing that crushed it to under
  // half a pocket, right in the stretch where it is supposed to be the loudest
  // thing on screen.
  return scuff * (1 - w) + rattle;
}

/** Ball radius: the track, then the bounce down into a pocket.
 *
 *  The drop height IS the bounce, rather than a separate glide with a decorative
 *  hop on top. That falls out of the model for free and gives the right sizes
 *  without tuning: the first rebound reaches E² = 36% of the drop, the next
 *  13%, then 4.7%, 1.7%. On a 163-unit radius that is 17 → 6 → 2 → 0.8 units:
 *  three or four bounces you can actually see, then it is in. */
export function ballRadius(x: number, R: number, seed = 0): number {
  // These are matched to what Wheel.tsx actually DRAWS (viewBox units, R=163):
  //   gold ball-track ring  156 .. 168     -> ride at 160.6
  //   pocket ring           104 .. 150     -> rest at 127.1, among the numbers
  //   hub                     0 ..  96
  // They were 0.92R = 149.4 and 0.615R = 100.2: the ball rode BELOW the track,
  // on the outer edge of the number ring, and came to rest at 100.2 — on the
  // HUB, underneath the numbers, in a place no pocket exists. A ball that
  // settles somewhere there is no pocket is about as fake as it gets, and no
  // amount of bounce physics fixes it.
  // R_POCKET comes from wheelGeometry, NOT from a number typed here. It used to
  // be a hand-copied 0.78 — the same value the geometry module held — and the
  // moment the geometry moved the ball stayed behind, because this is the copy
  // that actually places it.
  const R_TRACK = 0.985 * R, R_POCKET = REST_FRAC * R;
  // The micro-rattle's frequency is pinned so that it is EXACTLY zero at the
  // moment of the drop (sin(27π) = 0). At the old 140 it was mid-swing there,
  // leaving the radius 0.00355R below R_TRACK on the track side and exactly
  // R_TRACK on the bounce side — a 0.49px jump at the one instant the eye is
  // watching for an impact. Sub-pixel, but free to remove, and a discontinuity
  // at the drop is precisely the wrong place to have one.
  if (x <= XD) return R_TRACK + 0.004 * R * Math.sin(x * TRACK_RATTLE_K);
  const w = (x - XD) / (1 - XD);
  return R_POCKET + (R_TRACK - R_POCKET) * bounceHeight(w, seed);
}

/** Solve a spin that is guaranteed to land on `winner`. */
export function planSpin(winner: number, opts?: { quick?: boolean; rotorA0?: number; seconds?: number }): Spin {
  const rnd = (a: number, b: number) => a + Math.random() * (b - a);
  const base = opts?.quick ? 4.6 : 8.0;
  /* `seconds` is the SHARED-TABLE override (Phase E, plan §5.7, 2026-09-06) and it is
     optional precisely so the local path is untouched: omit it and this is the shipped line.
     On a shared table the wheel does not choose how long it turns for — the server does, by
     stamping `settles_at`, and the ball has to seat on that instant on every device at the
     table. So the caller derives the flight from the round's own stamps
     (`sharedTiming.flightSecondsFor`) and hands it in. The ±8% jitter below would be actively
     WRONG there: it exists so two consecutive spins do not take identical time on one phone,
     and on a shared table the constraint is the opposite — every phone must agree. */
  const T = opts?.seconds && opts.seconds > 0 ? opts.seconds : base * rnd(0.92, 1.08);   // ±8% — identical timing reads as fake
  // Laps must follow the duration, not be drawn beside it: with k independent
  // of T, Quick mode (T≈4.6s) launched at 4.75-8.75 rev/s and left the track at
  // 1.04-1.93 rev/s — roughly double the speed at which a real ball can still
  // be held. Tying k to T keeps launch ≈3.4-3.9 rev/s in BOTH modes.
  const k = Math.max(6, Math.round(T * 1.75 + rnd(-1, 1)));
  const rotorA0 = opts?.rotorA0 ?? Math.random() * TAU;
  const ballA0 = Math.random() * TAU;
  const eps = rnd(-0.3, 0.3) * POCKET_ARC;           // never dead-centre

  const idx = WHEEL.indexOf(winner);
  const phiWin = idx * POCKET_ARC + eps;
  const thetaEnd = rotorAngle(T, rotorA0) + phiWin;
  const back = mod(ballA0 - thetaEnd, TAU);
  const S = back + TAU * k;

  return { winner, T, S, phiWin, eps, ballA0, rotorA0, wobbleSeed: rnd(0, TAU) };
}

/** Ball angle at time t. After settling it is PARENTED to the rotor — the ball
 *  keeps moving with the wheel, which is what real roulette looks like. */
/** How much of the settle is spent easing from free flight into riding with the
 *  rotor. Without it the ball crept 0.09px on the last frame and then jumped
 *  1.85px in the other direction on the next one — a 1.77px/frame break, every
 *  single spin, at the exact instant the player is reading the number. */
const PARENT_BLEND = 0.10;

export function ballAngle(s: Spin, t: number): number {
  // Once settled the ball IS in a pocket and simply rides the wheel.
  if (t >= s.T) return rotorAngle(t, s.rotorA0) + s.phiWin;
  const x = t / s.T;
  const free = s.ballA0 - s.S * ease(x) + wobble(x, s.wobbleSeed);
  if (x <= 1 - PARENT_BLEND) return free;

  // Over the last stretch, cross-fade into the pocket's own motion. Both curves
  // already meet at x = 1 — that is what the spin solver guarantees — so this
  // changes nothing about WHERE the ball lands. Smoothstep has zero slope at
  // its end, so the ball's velocity arrives matching the rotor's instead of
  // colliding with it.
  const b = (x - (1 - PARENT_BLEND)) / PARENT_BLEND;
  const sm = b * b * (3 - 2 * b);
  const seated = rotorAngle(t, s.rotorA0) + s.phiWin;
  // Blend the WRAPPED difference. `free` and `seated` name the same place on
  // screen at x = 1, but only modulo 2π — in absolute terms they are k whole
  // laps apart, because `free` has been counting every revolution since launch.
  // Interpolating the raw difference therefore drags the ball through those k
  // turns during the fade: measured, it turned a 1.77px/frame break into a
  // 14.48px one. Wrapped, the difference is 0 at x = 1, so the ball is exactly
  // where the solver put it and its velocity arrives matching the rotor's.
  const d = mod(seated - free + Math.PI, TAU) - Math.PI;
  return free + d * sm;
}

/** Which pocket the ball is visually in at time t (used by the self-test). */
export function pocketAt(s: Spin, t: number): number {
  const rel = mod(ballAngle(s, t) - rotorAngle(t, s.rotorA0), TAU);
  return WHEEL[Math.round(rel / POCKET_ARC) % POCKETS];
}

export const randomNumber = () => WHEEL[Math.floor(Math.random() * POCKETS)];
